import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Edit2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import AuthDialog from './AuthDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_LOGO = 'https://customer-assets.emergentagent.com/job_5d93558c-03ee-4589-a69a-81ee86a30b68/artifacts/pn1o285k_ChatGPT%20Image%20Mar%202%2C%202026%2C%2003_22_50%20PM.png';

const defaultFooterContent = {
  description: 'Leading manufacturer of premium corrugated carton boxes and packaging solutions. Quality, sustainability, and innovation in every box.',
  address: 'SFNO 332/1B, Sri Visalakshi Nagar, Mettukuppam Main Road, Vanagaram, Chennai 600 095',
  phone: '+91-7299808164',
  email: 'Info@challengepack.in',
  weekdayHours: '9:00 AM - 5:30 PM',
  saturdayHours: '9:00 AM - 4:00 PM',
  sundayHours: 'Closed',
  copyright: 'Challenge Packaging Industries. All rights reserved.',
  logoUrl: '',
};

export default function Footer({ user, setUser }) {
  const [content, setContent] = useState(defaultFooterContent);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(defaultFooterContent);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState('login');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pages/footer`);
        if (r.data?.content) {
          const c = { ...defaultFooterContent, ...r.data.content };
          setContent(c);
          if (c.logoUrl) setLogoUrl(c.logoUrl);
        }
      } catch (error) { console.error('Failed to load footer content:', error); }
      // Fallback: if no footer logo, use navbar logo
      try {
        const r = await axios.get(`${API}/pages/navbar`);
        if (r.data?.content?.logoUrl) {
          setLogoUrl(prev => prev === DEFAULT_LOGO ? r.data.content.logoUrl : prev);
        }
      } catch (error) { console.error('Failed to load navbar logo:', error); }
    })();
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      const url = res.data.url.startsWith('http') ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      setEditData(prev => ({ ...prev, logoUrl: url }));
      toast.success('Logo uploaded!');
    } catch (error) { console.error('Upload failed:', error); toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/pages/footer`, { content: editData }, { headers: { Authorization: `Bearer ${token}` } });
      setContent(editData);
      if (editData.logoUrl) setLogoUrl(editData.logoUrl);
      toast.success('Footer saved!'); setEditOpen(false);
    } catch (error) { console.error('Save failed:', error); toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <footer className="border-t border-border/20 relative overflow-hidden">
      <div className="absolute inset-0 gradient-mesh"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        {user?.role === 'admin' && (
          <button onClick={() => { setEditData(content); setEditOpen(true); }}
            className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-primary/80 hover:bg-primary text-primary-foreground flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
            data-testid="edit-footer-btn"><Edit2 className="w-3.5 h-3.5" /></button>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <img src={logoUrl} alt="Challenge Packaging" className="h-14 w-14 rounded-lg mb-4" />
            <p className="text-muted-foreground mb-4 max-w-md text-sm">{content.description}</p>
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground whitespace-pre-line">{content.address}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                <a href={`tel:${content.phone}`} className="text-muted-foreground hover:text-primary transition-colors">{content.phone}</a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                <a href={`mailto:${content.email}`} className="text-muted-foreground hover:text-primary transition-colors">{content.email}</a>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4 text-foreground">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              {[{ to: '/', label: 'Home' }, { to: '/products', label: 'Products' }, { to: '/services', label: 'Services' }, { to: '/sustainability', label: 'Sustainability' }, { to: '/contact', label: 'Contact Us' }].map((link) => (
                <li key={link.to}><Link to={link.to} className="text-muted-foreground hover:text-primary transition-colors">{link.label}</Link></li>
              ))}
              {!user && (
                <li><span onClick={() => { setAuthMode('login'); setShowAuthDialog(true); }} className="text-muted-foreground hover:text-primary transition-colors cursor-pointer" data-testid="footer-admin-login">US</span></li>
              )}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4 flex items-center text-foreground"><Clock className="w-5 h-5 mr-2 text-primary" />Business Hours</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex justify-between"><span>Monday - Friday:</span><span className="text-foreground/80">{content.weekdayHours}</span></li>
              <li className="flex justify-between"><span>Saturday:</span><span className="text-foreground/80">{content.saturdayHours}</span></li>
              <li className="flex justify-between"><span>Sunday:</span><span className="text-foreground/80">{content.sundayHours}</span></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border/40 mt-8 pt-8 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} {content.copyright}</p>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg bg-card border-border/40 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Edit Footer</DialogTitle>
            <DialogDescription>Update footer content, contact info, and business hours.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Footer Logo</Label>
              <div className="flex items-center gap-3 mt-1">
                <img src={editData.logoUrl || logoUrl} alt="Logo" className="h-12 w-12 rounded-lg border border-border/30 object-contain" />
                <label className="cursor-pointer px-3 py-1.5 text-xs rounded-lg border border-primary/30 text-primary hover:bg-primary/10 transition-colors">
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploading} />
                  {uploading ? 'Uploading...' : 'Upload Logo'}
                </label>
              </div>
            </div>
            <div><Label className="text-xs">Description</Label><Textarea value={editData.description} onChange={(e) => setEditData({...editData, description: e.target.value})} rows={2} className="bg-background border-border/40 text-sm" /></div>
            <div><Label className="text-xs">Address</Label><Textarea value={editData.address} onChange={(e) => setEditData({...editData, address: e.target.value})} rows={2} className="bg-background border-border/40 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Phone</Label><Input value={editData.phone} onChange={(e) => setEditData({...editData, phone: e.target.value})} className="bg-background border-border/40 text-sm h-8" /></div>
              <div><Label className="text-xs">Email</Label><Input value={editData.email} onChange={(e) => setEditData({...editData, email: e.target.value})} className="bg-background border-border/40 text-sm h-8" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Mon-Fri</Label><Input value={editData.weekdayHours} onChange={(e) => setEditData({...editData, weekdayHours: e.target.value})} className="bg-background border-border/40 text-sm h-8" /></div>
              <div><Label className="text-xs">Saturday</Label><Input value={editData.saturdayHours} onChange={(e) => setEditData({...editData, saturdayHours: e.target.value})} className="bg-background border-border/40 text-sm h-8" /></div>
              <div><Label className="text-xs">Sunday</Label><Input value={editData.sundayHours} onChange={(e) => setEditData({...editData, sundayHours: e.target.value})} className="bg-background border-border/40 text-sm h-8" /></div>
            </div>
            <div><Label className="text-xs">Copyright</Label><Input value={editData.copyright} onChange={(e) => setEditData({...editData, copyright: e.target.value})} className="bg-background border-border/40 text-sm h-8" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border/30">
            <Button variant="outline" onClick={() => setEditOpen(false)} size="sm"><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>
            <Button onClick={handleSave} disabled={saving} size="sm" className="bg-primary"><Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} mode={authMode} setMode={setAuthMode} setUser={setUser} />
    </footer>
  );
}
